// src/resources/UpcomingExpensesList.js
import React, { useEffect, useState, useMemo } from "react";
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  TopToolbar,
  CreateButton,
  ExportButton,
  useRefresh,
  Pagination,
} from "react-admin";
import { db } from "../firebase/firebase";
import {
  doc,
  updateDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { getCurrentRole } from "../firebase/auth";

/** 🔹 Update Firestore status **/
const updateStatus = async (id, status, userEmail, rejectionReason = "") => {
  const ref = doc(db, "upcomingExpenses", id);
  await updateDoc(ref, {
    status,
    rejectionReason: rejectionReason || null,
    updatedBy: userEmail,
    updatedAt: serverTimestamp(),
  });
};

/** 🔹 Action buttons **/
const ExpenseActions = ({ record, onStatusChange }) => {
  const userEmail = localStorage.getItem("email");
  const [showReasonBox, setShowReasonBox] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  if (!record) return null;

  const handleClick = async (newStatus) => {
    if (isProcessing) return;
    if (newStatus === "rejected") {
      setShowReasonBox(true);
      return;
    }

    setIsProcessing(true);

    // ✅ Immediately remove from current tab
    onStatusChange?.("remove", record.id);

    try {
      await updateStatus(record.id, newStatus, userEmail);
      // ✅ Once Firestore update done, tell parent to switch tab
      onStatusChange?.(newStatus, record.id);
    } catch (err) {
      console.error("❌ Error updating status:", err);
      alert("Error updating status: " + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      alert("⚠️ Please mention a reason for rejection.");
      return;
    }

    setIsProcessing(true);
    onStatusChange?.("remove", record.id); // remove instantly

    try {
      await updateStatus(record.id, "rejected", userEmail, rejectionReason);
      setShowReasonBox(false);
      setRejectionReason("");
      onStatusChange?.("rejected", record.id); // switch tab to rejected
    } catch (err) {
      console.error("❌ Error rejecting:", err);
      alert("Error rejecting: " + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectCancel = () => {
    setShowReasonBox(false);
    setRejectionReason("");
  };

  const status = record.status?.toLowerCase();

  return (
    <div>
      {showReasonBox && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: 20,
              borderRadius: 10,
              width: "400px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            <h3 style={{ marginBottom: 10, color: "#dc2626" }}>
              ❌ Reason for Rejection
            </h3>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
              placeholder="Type your reason..."
              style={{
                width: "100%",
                borderRadius: 6,
                border: "1px solid #ccc",
                padding: "8px",
                resize: "none",
              }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: 12,
              }}
            >
              <button
                onClick={handleRejectCancel}
                style={{
                  padding: "6px 12px",
                  background: "#e5e7eb",
                  borderRadius: 6,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                style={{
                  padding: "6px 12px",
                  background: "#dc2626",
                  color: "#fff",
                  borderRadius: 6,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {status === "pending" && (
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            disabled={isProcessing}
            onClick={() => handleClick("approved")}
            style={{
              background: "#16a34a",
              color: "#fff",
              padding: "5px 10px",
              borderRadius: 6,
              border: "none",
              cursor: "pointer",
              opacity: isProcessing ? 0.6 : 1,
            }}
          >
            ✅ Approve
          </button>
          <button
            disabled={isProcessing}
            onClick={() => handleClick("rejected")}
            style={{
              background: "#dc2626",
              color: "#fff",
              padding: "5px 10px",
              borderRadius: 6,
              border: "none",
              cursor: "pointer",
              opacity: isProcessing ? 0.6 : 1,
            }}
          >
            ❌ Reject
          </button>
        </div>
      )}

      {status === "approved" && (
        <button
          disabled={isProcessing}
          onClick={() => handleClick("transferred")}
          style={{
            background: "#2563eb",
            color: "#fff",
            padding: "5px 10px",
            borderRadius: 6,
            border: "none",
            cursor: "pointer",
            opacity: isProcessing ? 0.6 : 1,
          }}
        >
          💰 Mark as Transferred
        </button>
      )}

      {status !== "pending" && status !== "approved" && (
        <span
          style={{
            textTransform: "capitalize",
            color:
              status === "transferred"
                ? "#2563eb"
                : status === "rejected"
                ? "#dc2626"
                : "#6b7280",
            fontWeight: 600,
          }}
        >
          {record.status}
        </span>
      )}
    </div>
  );
};



/** 🔹 Beautified Summary by Payment Type & Date **/
const SummaryByDate = ({ data }) => {
  const groupedByType = useMemo(() => {
    if (!data?.length) return {};

    const result = {};

    data.forEach((item) => {
      const type = item.paymentType || "Unknown";
      const dateKey = item.date || item.plannedDate;

      let formattedDate = "No Date";
      if (dateKey) {
        const d =
          typeof dateKey === "string"
            ? new Date(dateKey)
            : new Date(dateKey.seconds * 1000);
        formattedDate = d.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
        });
      }

      if (!result[type]) result[type] = {};
      result[type][formattedDate] =
        (result[type][formattedDate] || 0) + Number(item.amount || 0);
    });

    // Sort each payment type's dates
    Object.keys(result).forEach((type) => {
      const sorted = Object.entries(result[type]).sort(
        (a, b) =>
          new Date(a[0].replace(/(\d{2}) (\w{3})/, "$2 $1")) -
          new Date(b[0].replace(/(\d{2}) (\w{3})/, "$2 $1"))
      );
      result[type] = Object.fromEntries(sorted);
    });

    return result;
  }, [data]);

  if (!Object.keys(groupedByType).length) return null;

  return (
    <div
      style={{
        background: "#ffffff",
        padding: "18px 20px",
        borderRadius: 12,
        marginBottom: 20,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        fontFamily: "Inter, sans-serif",
      }}
    >
      <h3
        style={{
          marginBottom: 12,
          fontSize: "17px",
          fontWeight: 600,
          color: "#111827",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        📊 Expense Summary by Payment Type
      </h3>

      {Object.entries(groupedByType).map(([type, dates]) => (
        <div
          key={type}
          style={{
            marginBottom: 18,
            borderLeft: "4px solid #2563eb",
            paddingLeft: 10,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: "15px",
              color: "#2563eb",
              marginBottom: 6,
            }}
          >
            💳 {type}
          </div>

          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "14px",
              color: "#374151",
            }}
          >
            <tbody>
              {Object.entries(dates).map(([date, amount]) => (
                <tr key={date}>
                  <td style={{ padding: "4px 8px", width: "40%" }}>📅 {date}</td>
                  <td
                    style={{
                      padding: "4px 8px",
                      textAlign: "right",
                      fontWeight: 600,
                      color: "#111827",
                    }}
                  >
                    {amount.toLocaleString()} AED
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Subtotal per payment type */}
          <div
            style={{
              textAlign: "right",
              marginTop: 6,
              fontWeight: 600,
              color: "#16a34a",
            }}
          >
            Total:{" "}
            {Object.values(dates)
              .reduce((a, b) => a + b, 0)
              .toLocaleString()}{" "}
            AED
          </div>
        </div>
      ))}
    </div>
  );
};


/** 🔹 Status Tabs **/
const StatusTabs = ({ selected, onChange }) => {
  const tabs = ["pending", "approved", "transferred", "rejected"];
  const labels = {
    pending: "🕓 Pending",
    approved: "✅ Approved (Awaiting Transfer)",
    transferred: "💰 Funds Transferred",
    rejected: "❌ Rejected",
  };
  const colors = {
    pending: "#facc15",
    approved: "#22c55e",
    transferred: "#2563eb",
    rejected: "#dc2626",
  };

  return (
    <div style={{ display: "flex", gap: "10px", marginBottom: 16 }}>
      {tabs.map((tab) => (
        <button
          key={tab}
          onClick={() => onChange(tab)}
          style={{
            background: selected === tab ? colors[tab] : "#e5e7eb",
            color: selected === tab ? "#fff" : "#111827",
            border: "none",
            padding: "6px 14px",
            borderRadius: 20,
            cursor: "pointer",
            fontWeight: 500,
            boxShadow:
              selected === tab ? "0 2px 6px rgba(0,0,0,0.15)" : "none",
          }}
        >
          {labels[tab]}
        </button>
      ))}
    </div>
  );
};

/** 🔹 Main Component **/
export const UpcomingExpensesList = () => {
  const [role, setRole] = useState("");
  const [selectedTab, setSelectedTab] = useState("pending");
  const [data, setData] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const refresh = useRefresh();

  useEffect(() => {
    setRole((getCurrentRole() || "").toLowerCase());
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const colRef = collection(db, "upcomingExpenses");
        const q = query(colRef, where("status", "==", selectedTab));
        const snap = await getDocs(q);

        let docs = snap.docs.map((d) => {
          const data = d.data();

          // ✅ Prefer plannedDate, fallback to old date
          let expenseDate = data.plannedDate || data.date;

          if (typeof expenseDate === "string") {
            expenseDate = new Date(expenseDate);
          } else if (expenseDate?.seconds) {
            expenseDate = new Date(expenseDate.seconds * 1000);
          }

          return { id: d.id, ...data, expenseDate };
        });

        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);
          docs = docs.filter(
            (d) => d.expenseDate && d.expenseDate >= start && d.expenseDate <= end
          );
        }

        docs.sort((a, b) => (b.expenseDate || 0) - (a.expenseDate || 0));

        setData(docs);
      } catch (err) {
        console.error("❌ Error fetching expenses:", err);
      }
    };

    fetchData();
  }, [selectedTab, startDate, endDate, refresh]);

  /** ✅ Split views by role **/
  if (role === "md") {
    // MD view
    return (
      <div style={{ padding: "20px" }}>
        <StatusTabs selected={selectedTab} onChange={setSelectedTab} />

        <div style={{ display: "flex", gap: "10px", marginBottom: 16 }}>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc" }}
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc" }}
          />
        </div>

        <SummaryByDate data={data} />

        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            background: "#fff",
            borderRadius: 10,
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          }}
        >
          <thead>
            <tr style={{ background: "#f3f4f6", textAlign: "left" }}>
              <th style={{ padding: "8px" }}>Title</th>
              <th>Amount (AED)</th>
              <th>Payment Type</th>
              <th>Planned Date</th>
              <th>Description</th>
              <th>Status / Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((record) => (
              <tr key={record.id}>
                <td style={{ padding: "8px" }}>{record.title}</td>
                <td>{record.amount}</td>
                <td>{record.paymentType}</td>
                <td>
                  {record.plannedDate
                    ? new Date(record.plannedDate).toLocaleDateString()
                    : "-"}
                </td>
                <td>{record.remarks || record.description || "-"}</td>
                <td>
                  <ExpenseActions
  record={record}
  onStatusChange={(action, recordId) => {
    if (action === "remove") {
      // ✅ Immediately remove from list in-place and force rerender
      setData((prevData) => {
        const updated = prevData.filter((item) => item.id !== recordId);
        return [...updated]; // 👈 ensure new reference so React re-renders
      });
    } else if (["approved", "rejected", "transferred"].includes(action)) {
      // ✅ Auto-switch tab & re-fetch in background to stay synced
      setSelectedTab(action);
      setTimeout(() => refresh(), 400);
    }
  }}
/>

                </td>
              </tr>
            ))}
            {data.length === 0 && (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "16px" }}>
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  // ✅ Admin view (React-Admin List)
  return (
    <List
      title="Upcoming Expenses"
      perPage={50}
      pagination={<Pagination rowsPerPageOptions={[5, 10, 25, 50]} />}
      actions={
        <TopToolbar>
          <CreateButton />
          <ExportButton />
        </TopToolbar>
      }
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="title" label="Expense Title" />
        <NumberField source="amount" label="Amount (AED)" />
        <TextField source="paymentType" label="Payment Type" />
        <DateField source="plannedDate" label="Planned Date" />
        <TextField source="remarks" label="Description" />
        <TextField source="status" label="Status" />
      </Datagrid>
    </List>
  );
};
